import type { MenuProps } from 'antd';
import { sentenceCase } from '../helper';
import {
  WarningIcon,
  TruckIcon,
  ClockIcon,
  LocationIcon,
  AiStar,
  LowInventoryIcon,
  TemperatureGxpReportIcon,
  type IconProps,
} from '../../assets/icons/icons';
import { TriggerType } from '../static_data';

/**
 * Builds menu items for notification trigger types with icons and dividers
 *
 * @param notifications - Array of notification trigger type objects
 * @returns Array of menu items with dividers between them
 */
export const buildNotificationTriggerType = (
  notifications?: TriggerType[]
): MenuProps['items'] => {
  const iconMap: Record<
    string,
    React.ComponentType<Pick<IconProps, 'fill' | 'color' | 'width' | 'height'>>
  > = {
    WarningIcon,
    TruckIcon,
    ClockIcon,
    LocationIcon,
    AiStar,
    LowInventoryIcon,
    TemperatureGxpReportIcon,
  };

  const items: MenuProps['items'] = [];

  notifications?.forEach((item, index) => {
    const Icon = item?.icon ? iconMap[item.icon] : undefined;

    items.push({
      key: item.key,
      className: 'trigger-type-menu',
      icon: Icon ? (
        <Icon
          fill='var(--text-primary)'
          color='var(--text-primary)'
          width={20}
          height={20}
        />
      ) : (
        <div />
      ),
      label: (
        <div className='trigger-type-menu-label'>
          <p className='trigger-type-menu-title'>{item.title}</p>
          <p className='trigger-type-menu-text'>{item.text}</p>
        </div>
      ),
    });

    if (index < notifications.length - 1) {
      items.push({
        type: 'divider',
        key: `${item.key}-divider`,
        className: 'trigger-type-menu-divider',
      });
    }
  });

  return items;
};

interface RecordType {
  alert_type: string;
  is_delivery_confirmation: boolean;
  is_delivery_exception: boolean;
  is_pickup_confirmation: boolean;
  alert_types: Record<string, string>;
  alert_type_names: string[];
  select_all_alert_types: boolean;
  [key: string]: unknown;
}

export const getNotificationListTriggerType = (record: RecordType) => {
  if (!record) {
    return;
  }
  let title = '';
  let formatted = '';
  let tooltipList = '';
  let moreText = '';
  let count = 0;

  if (record.alert_type === 'PICKUP_DELIVERY') {
    title = 'Status progress';

    const mapping: Record<string, string> = {
      is_delivery_confirmation: 'Delivered',
      is_delivery_exception: 'Delivery exception',
      is_pickup_confirmation: 'Pick-up',
      is_created: 'Created',
      is_incomplete: 'Incomplete',
    };

    const matched = Object.keys(mapping)
      .filter((key) => record[key] === true)
      .map((key) => mapping[key]);

    if (matched.length > 0) {
      formatted = matched[0];
      tooltipList = matched.slice(1).join(', ');
      count = matched.length - 1;
    }
  } else if (record.alert_type === 'CRITICAL_ALERT') {
    title = 'Alert';

    const keys = Array.isArray(record.alert_type_names)
      ? record.alert_type_names.map((name) => sentenceCase(name))
      : [];
    if (keys.length > 0) {
      formatted = record?.select_all_alert_types ? 'All' : keys[0];
      tooltipList = !record?.select_all_alert_types
        ? keys.slice(1, 7).join(', ') + (keys.length > 7 ? ' ...' : '')
        : '';
      count = keys.length - 1;
    }
  }

  if (
    (count > 0 && !record.select_all_alert_types) ||
    (count > 0 && record.alert_type === 'PICKUP_DELIVERY')
  ) {
    moreText = `, +${count} more`;
  }

  return { title, formatted, tooltipList, moreText, count };
};
